import { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  schema?: object; // JSON-LD Structure
  noIndex?: boolean; // Prevent search engines from indexing this page
}

export const useSeo = ({
  title,
  description,
  keywords = [],
  ogImage = 'https://cdn.prod.website-files.com/651d8cc426674e7695b3aaf4/69202e1bd70e69b6aee76578_Pinaka.avif', // Default fallback
  ogType = 'website',
  schema,
  noIndex = false
}: SeoProps) => {
  useEffect(() => {
    // 1. Update Title
    document.title = title;

    // 2. Update Meta Tags
    const setMeta = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setProperty = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords.length > 0) {
      setMeta('keywords', keywords.join(', '));
    }

    // Handle Robots (Index/NoIndex)
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // 3. Update Open Graph / Twitter
    setProperty('og:title', title);
    setProperty('og:description', description);
    setProperty('og:image', ogImage);
    setProperty('og:type', ogType);
    setProperty('og:url', window.location.href);

    setProperty('twitter:title', title);
    setProperty('twitter:description', description);
    setProperty('twitter:image', ogImage);

    // 4. Update Canonical URL
    let linkCanonical = document.querySelector("link[rel='canonical']");
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    // Ensure we strip query params for the canonical version
    linkCanonical.setAttribute('href', window.location.origin + window.location.pathname);


    // 5. Inject JSON-LD Schema
    if (schema) {
      let script = document.querySelector('#json-ld-schema');
      if (!script) {
        script = document.createElement('script');
        script.id = 'json-ld-schema';
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    }

    // Cleanup not strictly necessary for meta tags in SPA unless tracking strict state,
    // but we leave schema clean up to avoid duplicate/conflicting schemas on route change.
    return () => {
       const script = document.querySelector('#json-ld-schema');
       if (script) {
         script.textContent = ''; // Clear content but keep element
       }
    };

  }, [title, description, keywords, ogImage, ogType, schema, noIndex]);
};